import React from 'react';
import { getIdentityToken } from '../services/AuthClient';
import { jwtDecode } from 'jwt-decode';

interface AuthenticationControllerProps {
    children: React.ReactNode;
}

interface AuthenticationControllerState {
    isLoggedIn: boolean,
    skipLogin: boolean,
    authenticatedUserAlias: string,
    token: string
}

export interface UserInfo {
    userAlias: string,
    userToken: string
}

const DEFAULT_USER_INFO: UserInfo = {
    userAlias: 'Guest',
    userToken: ''
}

const AuthenticatedUserContext = React.createContext<UserInfo>(DEFAULT_USER_INFO);

/** A component that wraps the Application to provide Authentication and will only
 * render the application if there is a fully authenticated user.
 */
class AuthenticationController extends
    React.Component<AuthenticationControllerProps, AuthenticationControllerState> {
    private authInterval: ReturnType<typeof setInterval> | null = null;

    constructor (props: AuthenticationControllerProps) {
        super(props);
        this.state = {
            isLoggedIn: false,
            skipLogin: window.location?.pathname?.startsWith('/error') || false,
            authenticatedUserAlias: DEFAULT_USER_INFO.userAlias,
            token: DEFAULT_USER_INFO.userToken
        };
        this.verifyAuthentication = this.verifyAuthentication.bind(this)
    }

    componentDidMount () {
        const REFRESH_CREDS_INTERVAL = 300000; // 5 minute intervals
        this.verifyAuthentication();
        this.authInterval = setInterval(this.verifyAuthentication, REFRESH_CREDS_INTERVAL);
    }

    componentWillUnmount () {
        // Clear authentication interval to prevent memory leaks
        if (this.authInterval) {
            clearInterval(this.authInterval);
            this.authInterval = null;
        }
    }

    verifyAuthentication () {
        if (process.env.NODE_ENV === 'development' && window.location.hostname === 'localhost') {
            this.setState({ isLoggedIn: true, authenticatedUserAlias: 'local-dev', token: '' });
            return Promise.resolve();
        }
        return getIdentityToken().then((data) => {
            if (data) {
                const decodedJwt = jwtDecode<any>(data);
                if (decodedJwt && decodedJwt.sub) {
                    this.setState({
                        isLoggedIn: true,
                        authenticatedUserAlias: decodedJwt.sub,
                        token: data
                    });
                }
            }
        })
            .catch((err) => {
                this.setState({
                    isLoggedIn: false
                });
                console.error("Authentication error occurred.", err);
            });
    }

    render () {
        const shouldRenderApp = this.state.isLoggedIn || this.state.skipLogin;

        if (shouldRenderApp) {
            const authenticatedUserInfo: UserInfo = {
                userAlias: this.state.authenticatedUserAlias,
                userToken: this.state.token
            };
            return (
                <AuthenticatedUserContext.Provider value={authenticatedUserInfo}>
                    {this.props.children}
                </AuthenticatedUserContext.Provider>
            );
        } else {
            return (
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '100vh',
                    fontSize: '18px'
                }}>
                    Loading authentication...
                </div>
            );
        }
    }
}

export {
    AuthenticationController,
    AuthenticatedUserContext
};
